import React from 'react';
import { connectSortBy } from 'react-instantsearch-dom';
import cx from 'classnames';
import {
  flow,
  map,
  groupBy,
  mapValues,
  merge,
  omitBy,
  isNil,
  values,
} from 'lodash/fp';

import { useOverlayThatClosesOnOutsideClick } from '../../hooks/useOverlayThatClosesOnOutsideClick';

import './SortSelector.scss';

export const SortSelector = connectSortBy(
  ({ items, refine, currentRefinement, defaultRefinement }) => {
    const { isOpen, toggle, containerRef } = useOverlayThatClosesOnOutsideClick(
      { containerRef, isOpen, close: toggle }
    );

    const isInUse = currentRefinement !== defaultRefinement;

    const options = mapItemsToDropdownOptions(items);

    return (
      <div
        className="sort-selector hidden lg:flex w-full lg:flex-1 ml-2"
        ref={containerRef}
      >
        <div
          className={cx(
            'flex-1 cursor-pointer',
            (isOpen || isInUse) && 'text-smurfs-gold'
          )}
          onClick={toggle}
        >
          <span>
            Sort By
            <i className="fas fa-sort-alpha-down" />
          </span>
        </div>
        {isOpen && (
          <div className="overlay overlay--left">
            <div className="item-list">
              {options.map(({ value, label, direction }) => (
                <SortSelectorOption
                  key={value}
                  value={value}
                  label={label}
                  selectedDirection={direction}
                  refine={refine}
                  defaultRefinement={defaultRefinement}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
);

function SortSelectorOption({
  value,
  label,
  refine,
  selectedDirection = undefined,
}) {
  const isSelected = selectedDirection !== undefined;

  function getSortIcon() {
    if (!isSelected) {
      return 'fas fa-sort';
    }

    return selectedDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
  }

  function applySort() {
    // Switch to ASC if it's currently DESC
    if (selectedDirection === 'desc') {
      refine(value + '_asc');
      return;
    }

    // If it was ASC, or wasn't selected, select DESC
    refine(value + '_desc');
  }

  return (
    <div
      onClick={applySort}
      className={cx('item', isSelected && 'item--selected')}
    >
      {label}
      <i className={cx('ml-1', getSortIcon())} />
    </div>
  );
}

function mapItemsToDropdownOptions(items) {
  return flow(
    map(item => {
      const [label, direction] = item.label.split('|');
      const prefix = item.value
        .split('_')
        .slice(0, -1)
        .join('_');
      return {
        direction: item.isRefined ? direction : undefined,
        value: prefix,
        label,
      };
    }),
    groupBy('value'),
    mapValues(
      flow(
        group => merge(...group),
        omitBy(isNil)
      )
    ),
    values
  )(items);
}
